import { useEffect, useRef, useCallback } from 'react';
import { authService } from '../components/auth';

const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds
const DEBOUNCE_TIME = 1000; // Debounce activity events by 1 second to reduce re-renders

export const useInactivityTimeout = () => {
  const timeoutRef = useRef(null);
  const debounceRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const resetTimeout = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Update last activity time
    lastActivityRef.current = Date.now();

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      // Log out user
      authService.logout();
      window.location.href = '/';
    }, INACTIVITY_TIMEOUT);
  }, []);

  const handleActivity = useCallback(() => {
    // Debounce activity events to reduce unnecessary timer resets
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      resetTimeout();
    }, DEBOUNCE_TIME);
  }, [resetTimeout]);

  useEffect(() => {
    // Set initial timeout
    resetTimeout();

    // Track user activity with passive listeners for better performance
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [handleActivity, resetTimeout]);

  return null;
};
